import { c as _c } from "react/compiler-runtime";
import type { BigIntish } from '@morpho-org/blue-sdk';
import { toBigInt } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { formatters } from '@morpho-org/ui-v6/utils';
import { memo, useMemo } from 'react';
import type { VaultV2Caps, VaultV2PendingTimelockDecodedData } from '../../types/pendingTimelocks';
import { getTimelockFunctionConfig } from '../utils';
import { StyledVaultPendingTimelock } from '../VaultV2PendingTimelockStyled';
import AdapterItemLink from './AdapterItemLink';
import CollateralTokenLink from './CollateralTokenLink';
import MarketItemLink from './MarketItemLink';
import VaultPendingTimelockHeader from './VaultV2PendingTimelockHeader';
interface VaultV2PendingTimelockChangeRelativeCapProps {
  newValue: BigIntish;
  functionName: string;
  capId: string;
  caps?: VaultV2Caps;
  data: Extract<VaultV2PendingTimelockDecodedData, {
    __typename: 'VaultV2IncreaseCapPendingData';
  }>['config']['data'];
}
const VaultV2PendingTimelockChangeRelativeCap = t0 => {
  const $ = _c(35);
  const {
    newValue,
    functionName,
    capId,
    caps: t1,
    data
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const caps = t2;
  let t3;
  if ($[2] !== capId || $[3] !== caps) {
    let t4;
    if ($[5] !== capId) {
      t4 = cap => cap.id === capId;
      $[5] = capId;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    t3 = caps.find(t4);
    $[2] = capId;
    $[3] = caps;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const currentCap = t3;
  let t4;
  if ($[7] !== functionName) {
    t4 = getTimelockFunctionConfig({
      functionName
    });
    $[7] = functionName;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const {
    label,
    criticality
  } = t4;
  let t5;
  if ($[9] !== currentCap?.relativeCap) {
    t5 = formatters.percent.default("0").trailingZero(false).digits(2).unit("%").of(toBigInt(currentCap?.relativeCap), 18);
    $[9] = currentCap?.relativeCap;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const formattedCurrentRelativeCap = t5;
  let t6;
  if ($[11] !== newValue) {
    t6 = formatters.percent.default("0").trailingZero(false).digits(2).unit("%").of(toBigInt(newValue), 18);
    $[11] = newValue;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const formattedNextRelativeCap = t6;
  let t7;
  if ($[13] !== criticality || $[14] !== label) {
    t7 = <VaultPendingTimelockHeader title={label} criticality={criticality} />;
    $[13] = criticality;
    $[14] = label;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== data) {
    t8 = data?.__typename === "MarketV1CapData" && <StyledVaultPendingTimelock.Row><span className="text-2xs text-foreground">Item</span><span className="text-2xs text-foreground"><MarketItemLink market={data.market} /></span></StyledVaultPendingTimelock.Row>;
    $[16] = data;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== data) {
    t9 = data?.__typename === "AdapterCapData" && <StyledVaultPendingTimelock.Row><span className="text-2xs text-foreground">Item</span><span className="text-2xs text-foreground min-w-0"><AdapterItemLink adapter={data.adapter} /></span></StyledVaultPendingTimelock.Row>;
    $[18] = data;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== data) {
    t10 = data?.__typename === "CollateralCapData" && data.collateralToken && <StyledVaultPendingTimelock.Row><span className="text-2xs text-foreground">Item</span><CollateralTokenLink collateralToken={data.collateralToken} /></StyledVaultPendingTimelock.Row>;
    $[20] = data;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== currentCap || $[23] !== formattedCurrentRelativeCap) {
    t11 = isDefined(currentCap) && <StyledVaultPendingTimelock.Row><span className="text-2xs text-foreground">Current cap</span><span className="text-2xs text-foreground">{formattedCurrentRelativeCap}</span></StyledVaultPendingTimelock.Row>;
    $[22] = currentCap;
    $[23] = formattedCurrentRelativeCap;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <span className="text-2xs text-foreground">New cap</span>;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== formattedNextRelativeCap) {
    t13 = <StyledVaultPendingTimelock.Row>{t12}<span className="text-2xs text-foreground">{formattedNextRelativeCap}</span></StyledVaultPendingTimelock.Row>;
    $[26] = formattedNextRelativeCap;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t10 || $[29] !== t11 || $[30] !== t13 || $[31] !== t7 || $[32] !== t8 || $[33] !== t9) {
    t14 = <div className="flex flex-col">{t7}{t8}{t9}{t10}{t11}{t13}</div>;
    $[28] = t10;
    $[29] = t11;
    $[30] = t13;
    $[31] = t7;
    $[32] = t8;
    $[33] = t9;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  return t14;
};
export default memo(VaultV2PendingTimelockChangeRelativeCap);